import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import { toastFailed, toastSuccess } from 'utils/toast'
import { getKongLocationAll } from 'features/Location/services/location'
import {
  AdjacentHubPayloadType,
  GetAdjacentHubDetailResponseType,
  getAdjacentHubDetail,
  patchAdjacentHubStatus,
  postAdjacentHub,
  putAdjacentHub,
} from 'features/Location/services/adjacentHub'
import { AdjacentHubLocationOptionType } from 'features/Location/@types/adjacentHub'
import {
  getValidAdjacentLocations,
  toAdjacentLocationNameMap,
} from 'features/Location/utils/adjacentHub'
import { fetchAdjacentHubList } from '../list/thunk'
import { setAdjacentLocationNames } from '../list/slice'
import {
  SLICE_NAME,
  reset,
  setForm,
  setHubOptions,
  setIsLoadingDetail,
  setIsLoadingHubOptions,
  setIsLoadingNearbyHubOptions,
  setIsSubmitting,
  setNearbyHubOptions,
} from './slice'

const HUB_OPTIONS_LIMIT = 50

// Ratio inputs accept both "1,5" (Figma/ID locale) and "1.5".
export const parseDecimal = (value: string) => Number(value.replace(',', '.'))

const getHubOptions = async (name?: string): Promise<AdjacentHubLocationOptionType[]> => {
  const { data } = await getKongLocationAll({
    params: { limit: HUB_OPTIONS_LIMIT, page: 0, type: 'HUB', active: true, name },
  })

  return data.data.location.map((location) => ({
    location_id: location.location_id,
    name: location.location_name,
  }))
}

export const fetchHubOptions = createAsyncThunk(
  `${SLICE_NAME}/fetchHubOptions`,
  async (name: string | undefined, { dispatch }) => {
    dispatch(setIsLoadingHubOptions(true))
    try {
      dispatch(setHubOptions(await getHubOptions(name)))
    } catch (error) {
      callErrorMsg(error)
    }
    dispatch(setIsLoadingHubOptions(false))
  },
)

export const fetchNearbyHubOptions = createAsyncThunk(
  `${SLICE_NAME}/fetchNearbyHubOptions`,
  async (name: string | undefined, { dispatch }) => {
    dispatch(setIsLoadingNearbyHubOptions(true))
    try {
      dispatch(setNearbyHubOptions(await getHubOptions(name)))
    } catch (error) {
      callErrorMsg(error)
    }
    dispatch(setIsLoadingNearbyHubOptions(false))
  },
)

const mapDetailToForm = (detail: GetAdjacentHubDetailResponseType['data']) => ({
  maxDistance: String(detail.max_distance),
  maxAdditionalSla: String(detail.max_additional_sla),
  maxDriverRatio: String(detail.max_driver_ratio),
  maxPickerRatio: String(detail.max_picker_ratio),
  quotaAllowed: String(detail.quota_allowed),
  adjacentLocationIds: getValidAdjacentLocations(detail.adjacent_location).map(
    ({ location_id }) => location_id,
  ),
})

export const fetchAdjacentHubDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchAdjacentHubDetail`,
  async (locationId: number, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoadingDetail(true))
    try {
      const { data } = await getAdjacentHubDetail(locationId)
      // Status isn't in the detail response, so the row's status from openEdit stays.
      dispatch(setForm(mapDetailToForm(data.data)))
      // Lets selected hubs outside the fetched options still show their names.
      dispatch(setAdjacentLocationNames(toAdjacentLocationNameMap(data.data.adjacent_location)))
      dispatch(setIsLoadingDetail(false))
      return data
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoadingDetail(false))
      return rejectWithValue(error)
    }
  },
)

const saveAdjacentHub = (mode: 'CREATE' | 'EDIT', payload: AdjacentHubPayloadType) =>
  mode === 'EDIT' ? putAdjacentHub({ payload }) : postAdjacentHub({ payload })

export const submitAdjacentHub = createAsyncThunk(
  `${SLICE_NAME}/submitAdjacentHub`,
  async (_, { dispatch, getState, rejectWithValue }) => {
    const {
      adjacentHubCreateEdit: { form, mode, initialStatus },
    } = getState() as StoreStateType
    if (!form.hub) return rejectWithValue('hub is required')

    const payload: AdjacentHubPayloadType = {
      location_id: form.hub.location_id,
      max_distance: parseDecimal(form.maxDistance),
      adjacent_location_ids: form.adjacentLocationIds,
      max_driver_ratio: parseDecimal(form.maxDriverRatio),
      max_picker_ratio: parseDecimal(form.maxPickerRatio),
      max_additional_sla: Number(form.maxAdditionalSla),
      quota_allowed: Number(form.quotaAllowed),
    }
    const isStatusChanged = mode === 'EDIT' && form.status !== initialStatus

    const failSubmit = (error: unknown) => {
      dispatch(setIsSubmitting(false))
      // The config may already be saved when only the status call failed; refresh so the table is accurate.
      dispatch(fetchAdjacentHubList())
      return rejectWithValue(error)
    }

    dispatch(setIsSubmitting(true))
    try {
      const { data } = await saveAdjacentHub(mode, payload)
      if (data.error?.status) {
        toastFailed(data.error.message)
        return failSubmit(data.error)
      }

      // Status has its own endpoint, so it's sent after the config is saved.
      if (isStatusChanged) {
        const { data: statusData } = await patchAdjacentHubStatus({
          payload: { location_id: payload.location_id, status: form.status },
        })
        if (statusData.error?.status) {
          toastFailed(statusData.error.message)
          return failSubmit(statusData.error)
        }
      }

      toastSuccess(data.data.message)
      dispatch(reset())
      dispatch(fetchAdjacentHubList())
      return data
    } catch (error) {
      callErrorMsg(error)
      return failSubmit(error)
    }
  },
)
